import { useState } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { useDeviceFormCtx, useFormatUpdateDeviceInput } from '../DeviceFormProvider';
import { NvrFullDto } from '@/lib/integrationTypes/ihub-devices';
import { useReadExternalConnection } from '@/hooks/externalConnectionsApi';
import { DeviceConnectionChoices } from '../../components/DeviceConnectionChoices';
import DeviceVariablesForm from '../../IntegrationVariables/DeviceForm';
import { FormField, FormItem, FormControl, FormLabel, FormMessage } from '@/components/ui/form';
import BridgeRouteSelector from '@/features/EdgeLink/BridgeRouteSelector';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { FeatureFlags } from '@/lib/feature-flag';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import DevicesMap from '../../DevicesMap';
import { DeleteDeviceButton } from '../../components/DeleteDeviceModel';
import { MoveDeviceButton } from '../components/MoveDeviceDialog';
import { useSite } from '@/hooks/siteHooks';
import { MapPin, Network, ChevronDown, Trash2, Settings, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

// Helper to check if a route value is valid (not empty, not Guid.Empty)
const isValidRoute = (value: string | null | undefined): boolean =>
  Boolean(value && value !== '00000000-0000-0000-0000-000000000000');

type ConnectionsAndSettingsTabProps = Pick<
  NvrFullDto,
  'id' | 'nvrId' | 'projectId' | 'customerId' | 'integration' | 'externalConnectionLimitedDto' | 'deviceCreationChoices' | 'routeNamespaceId'
>;

// Reusable collapsible section wrapper
export const CollapsibleSection = ({
  title,
  description,
  icon,
  defaultOpen = false,
  children,
  variant = 'default',
}: {
  title: string;
  description?: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
  variant?: 'default' | 'danger';
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div
        className={cn(
          'rounded-xl border px-5 py-4',
          variant === 'danger'
            ? 'border-red-500/20 bg-red-950/20'
            : 'border-green-darker bg-green-darker'
        )}
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between text-left transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3">
              {icon}
              <div className="flex flex-col">
                <span className="text-base font-semibold">{title}</span>
                {description && (
                  <span className="text-sm text-muted/60">{description}</span>
                )}
              </div>
            </div>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform duration-200',
                isOpen && 'rotate-180'
              )}
            />
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="mt-4">{children}</div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export const ConnectionsAndSettingsTab = (props: ConnectionsAndSettingsTabProps) => {
  const { id, nvrId, projectId, customerId, externalConnectionLimitedDto, deviceCreationChoices, integration, routeNamespaceId } = props;
  const form = useDeviceFormCtx();
  const formatUpdateInput = useFormatUpdateDeviceInput(id);

  // Check if device is connected to a VMS
  const hasVmsConnection = Boolean(externalConnectionLimitedDto?.id);

  // Only fetch external connection if device is connected to one
  const extConnQuery = useReadExternalConnection({
    id: externalConnectionLimitedDto?.id ?? '',
  });
  const extConn = extConnQuery.data;

  // Connection settings for non-VMS devices
  const connSettings = useController({
    control: form.control,
    name: 'connectionSettings',
  });

  // Address for location settings
  const address = useController({
    name: 'address',
    control: form.control,
  });

  // EdgeLink state
  const routeValue = useWatch({ control: form.control, name: 'routeNamespaceId' });
  const hasExistingRoute = isValidRoute(routeValue);
  const edgeLinkEnabled = useFeatureFlagEnabled(FeatureFlags.EdgeLink);

  // Site query
  const siteQuery = useSite({
    siteId: projectId || '',
    companyId: customerId || '',
    enabled: !!(projectId && customerId),
  });

  return (
    <div className="mx-auto max-w-screen-2xl p-6">
      <div className="space-y-4">
        {/* Connection Info - Collapsible, default open */}
        <CollapsibleSection
          title="Connection"
          description={hasVmsConnection ? 'VMS connection details' : 'Configure connection settings for this device'}
          icon={<Globe className="h-5 w-5 text-lime" />}
          defaultOpen={true}
        >
          {hasVmsConnection ? (
            <DeviceConnectionChoices
              deviceCreationChoices={deviceCreationChoices ?? {}}
              externalConnection={extConn?.externalConnection}
              isLoading={extConnQuery.isLoading}
              onChoicesChange={(choices) => {
                form.setValue('deviceCreationChoices', choices as Record<string, string>, {
                  shouldDirty: true,
                  shouldTouch: true,
                });
              }}
            />
          ) : (
            connSettings.field.value && (
              <DeviceVariablesForm
                integration={integration}
                hideSubmit={true}
                onVariableSubmit={(data) => {
                  form.setValue('connectionSettings', data, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
                onFormChange={(data) => {
                  form.setValue('connectionSettings', data, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
                border={false}
                defaultValues={connSettings.field.value}
                routeNamespaceId={routeNamespaceId}
              />
            )
          )}
        </CollapsibleSection>

        {/* Device Details - Collapsed by default */}
        <CollapsibleSection
          title="Device Details"
          description="Configure basic device information and settings"
          icon={<Settings className="h-5 w-5 text-muted-foreground" />}
        >
          <div className="space-y-4">
            <div className="flex flex-wrap gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>Device Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter device name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="nvrId"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>Serial Number</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter serial number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter device description or notes"
                      className="min-h-[120px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <MapPin className="h-5 w-5" />
                <div>
                  <p className="text-sm font-medium">Assigned Site</p>
                  {siteQuery.data ? (
                    <Link
                      to={`/sites/${projectId}`}
                      className="text-sm hover:underline"
                    >
                      {siteQuery.data.name}
                    </Link>
                  ) : (
                    <p className="text-sm">
                      {siteQuery.isLoading ? 'Loading...' : 'No site assigned'}
                    </p>
                  )}
                </div>
              </div>
              <MoveDeviceButton deviceId={id} currentProjectId={projectId} />
            </div>
          </div>
        </CollapsibleSection>

        {/* Location Settings - Collapsed by default */}
        <CollapsibleSection
          title="Location Settings"
          description="Set the physical location of this device"
          icon={<MapPin className="h-5 w-5 text-muted-foreground" />}
        >
          <DevicesMap address={address.field.value} setAddress={address.field.onChange} />
        </CollapsibleSection>

        {/* EdgeLink Connection - Collapsed by default */}
        {edgeLinkEnabled && !hasVmsConnection && (
          <CollapsibleSection
            title="EdgeLink Connection"
            description={hasExistingRoute ? 'Route configured for this device' : 'Route traffic through a secure tunnel'}
            icon={
              <Network
                className={cn(
                  'h-5 w-5 transition-colors',
                  hasExistingRoute ? 'text-lime' : 'text-muted-foreground'
                )}
              />
            }
            defaultOpen={hasExistingRoute}
          >
            <FormField
              control={form.control}
              name="routeNamespaceId"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <BridgeRouteSelector
                      value={field.value}
                      onChange={(value) => field.onChange(value ?? '')}
                      placeholder="Select EdgeLink route..."
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </CollapsibleSection>
        )}

        {/* Danger Zone - Collapsed by default, no Configure Alerting */}
        {nvrId && (
          <CollapsibleSection
            title="Danger Zone"
            description="Irreversible and destructive actions"
            icon={<Trash2 className="h-5 w-5 text-red-400" />}
            variant="danger"
          >
            <div className="flex items-center justify-between rounded-lg border border-red-500/20 p-4">
              <div>
                <p className="text-sm font-medium">Delete Device</p>
                <p className="text-sm text-muted/60">This will delete your device and all of its data</p>
              </div>
              <DeleteDeviceButton id={id} getInput={() => formatUpdateInput(form.getValues())} />
            </div>
          </CollapsibleSection>
        )}
      </div>
    </div>
  );
};
